/**
 * Onboarding wizard page (Phase 7 + waha-connector-delta.md §5).
 *
 * First-run only: a workspace that has connected anything is handed to the
 * dashboard instead (see hasConnection), because everything past the connect
 * step — importing history, minting an MCP key — is optional and reachable
 * later from /sources and /settings.
 *
 * The connect step forks by app rather than adding a fifth step, so supporting
 * a second connector never costs every user an extra screen.
 */
import { Navigate, useNavigate } from '@tanstack/react-router';
import { MessageCircle, NotebookPen, Send, Slack } from 'lucide-react';
import { useState } from 'react';
import { ConnectOption } from '@/components/onboarding/connect-option';
import { SignalImport } from '@/components/onboarding/signal-import';
import { StepConnect } from '@/components/onboarding/step-connect';
import { StepGranolaConnect } from '@/components/onboarding/step-granola-connect';
import { StepImport } from '@/components/onboarding/step-import';
import { StepMcp } from '@/components/onboarding/step-mcp';
import { StepSignalConnect } from '@/components/onboarding/step-signal-connect';
import { StepSlackConnect } from '@/components/onboarding/step-slack-connect';
import { StepWorkspace } from '@/components/onboarding/step-workspace';
import { PageHeader } from '@/components/patterns/page-header';
import { Stepper } from '@/components/patterns/stepper';
import { Skeleton } from '@/components/ui/skeleton';
import { useConnectionStatus } from '@/lib/connections';

// ---------------------------------------------------------------------------
// Wizard step definitions
// ---------------------------------------------------------------------------

const STEPS = [
  { id: 'workspace', label: 'Workspace', sub: 'Named & ready' },
  { id: 'link', label: 'Connect', sub: 'WhatsApp, Signal, Slack or Granola' },
  { id: 'import', label: 'Import history', sub: 'Optional' },
  { id: 'connect', label: 'Connect', sub: 'Get your MCP key' },
] as const;

type StepId = (typeof STEPS)[number]['id'];

/** Which app/source the connect step is currently showing. */
type Connector = 'whatsapp' | 'signal' | 'slack' | 'granola';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OnboardingPage() {
  const navigate = useNavigate();

  // A connector may be unconfigured (503) — the hook never lets one block the
  // wizard, and settles before first render so the page can't flip after paint.
  const { connected, isLoading } = useConnectionStatus();

  // null means "hasn't touched the wizard yet" — the state the hand-off below
  // keys on. Only the user's own Continue/Back moves them between steps.
  const [localStep, setLocalStep] = useState<number | null>(null);
  const [connector, setConnector] = useState<Connector | null>(null);

  const currentStep = localStep ?? 0;

  function advance() {
    setLocalStep((prev) => (prev ?? currentStep) + 1);
  }

  function back() {
    setLocalStep((prev) => Math.max(0, (prev ?? currentStep) - 1));
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-48" />
        <div className="mt-10 grid grid-cols-[232px_1fr] items-start gap-14">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  // Already connected and arriving fresh (a bookmark, a restored tab, a
  // post-sign-in bounce) — setup is done as far as this wizard is concerned,
  // whether or not they ever ran an import. Gated on localStep so that
  // connecting DURING the wizard, which flips `connected` mid-flow, never
  // yanks the user out of the steps they're walking through.
  if (localStep === null && connected) {
    return <Navigate to="/dashboard" replace />;
  }

  const step = STEPS[currentStep] ?? STEPS[0];
  const stepId: StepId = step.id;

  return (
    <div>
      <PageHeader title="Set up Brain" subtitle="Connect your group chats and start searching." />

      <div className="mt-10 grid grid-cols-[232px_1fr] items-start gap-14">
        {/* Left rail — vertical progress stepper */}
        <div className="sticky top-[92px]">
          <Stepper
            steps={STEPS.map((s) => ({ label: s.label, sub: s.sub }))}
            current={currentStep}
            onStepClick={(i) => i <= currentStep && setLocalStep(i)}
          />
        </div>

        {/* Right — active step content */}
        <div className="min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[12px] uppercase tracking-[0.05em] text-faint">
              Step {currentStep + 1} of 4 — {step.label}
            </span>
            {currentStep > 0 && (
              <button
                type="button"
                onClick={back}
                className="text-[13px] text-faint transition-colors hover:text-foreground"
              >
                ← Back
              </button>
            )}
          </div>

          <div className="mt-4">
            {stepId === 'workspace' && <StepWorkspace onNext={advance} />}

            {stepId === 'link' &&
              (connector === null ? (
                <div className="space-y-4">
                  <p className="text-[14px] text-muted-foreground">
                    Where should Brain look for content? You can connect another source later.
                  </p>
                  <ConnectOption
                    icon={MessageCircle}
                    tone="green"
                    title="WhatsApp"
                    body="Connect your own number by QR, or use Brain's number in a group. Recent history is backfilled when you connect."
                    onClick={() => setConnector('whatsapp')}
                  />
                  <ConnectOption
                    icon={Send}
                    tone="green"
                    title="Signal"
                    body="Link your own account by QR, or use Brain's number in a group. Once connected, you can import a Signal backup to search older conversations."
                    onClick={() => setConnector('signal')}
                  />
                  <ConnectOption
                    icon={Slack}
                    tone="green"
                    title="Slack"
                    body="Connect your Slack account by OAuth — every channel and DM you're in is indexed automatically."
                    onClick={() => setConnector('slack')}
                  />
                  <ConnectOption
                    icon={NotebookPen}
                    tone="green"
                    title="Granola"
                    body="Paste a personal API key to index your meeting notes and transcripts automatically."
                    onClick={() => setConnector('granola')}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={() => setConnector(null)}
                    className="text-[13px] text-faint transition-colors hover:text-foreground"
                  >
                    ← Choose a different app
                  </button>
                  {connector === 'slack' ? (
                    <StepSlackConnect />
                  ) : connector === 'signal' ? (
                    <StepSignalConnect onNext={advance} />
                  ) : connector === 'granola' ? (
                    // Granola has no import step (it self-seeds on connect, no
                    // user-picked history like WhatsApp/Signal) — advance twice to
                    // land straight on the MCP step, skipping the blank 'import'
                    // one. Slack skips it too, but by leaving the SPA entirely for
                    // OAuth; Granola's form never leaves the SPA, so onNext really
                    // does fire and must jump the empty step itself.
                    <StepGranolaConnect
                      onNext={() => {
                        advance();
                        advance();
                      }}
                    />
                  ) : (
                    <StepConnect onNext={advance} />
                  )}
                </div>
              ))}

            {/* Import belongs to the account they just connected, so the
                importer follows the connector rather than the app assuming a
                WhatsApp export. Optional either way — Signal's has no skip of
                its own, so the wizard supplies one. Slack has no import at all
                (it indexes from connect and its OAuth leaves the wizard), so it
                never reaches this step — the explicit whatsapp branch keeps the
                export importer from ever showing for it. */}
            {stepId === 'import' &&
              (connector === 'signal' ? (
                <div className="space-y-4">
                  <SignalImport onDone={advance} />
                  <button
                    type="button"
                    onClick={advance}
                    className="text-[13px] text-faint transition-colors hover:text-foreground"
                  >
                    Skip for now — you can import later from Sources
                  </button>
                </div>
              ) : connector === 'whatsapp' ? (
                <StepImport defaultCollectionTitle="" onNext={advance} onSkip={advance} />
              ) : null)}

            {stepId === 'connect' && <StepMcp onDone={() => void navigate({ to: '/dashboard' })} />}
          </div>
        </div>
      </div>
    </div>
  );
}
